import React from 'react';

/**
 * Logotype — Unified app logotype per design-system.md Section 6
 *
 * The badge replaces the corresponding letter in the app's name to form
 * a single word: [U-badge]ncode, [G-badge]iraffe, FLU[X-badge], etc.
 *
 * Embeds an iOS squircle (n=4 superellipse) SVG clipPath so every badge
 * at every size inherits the same continuous-curve mask.
 *
 * Props:
 *   app: string — one of: uncode, giraffe, framed, cult, soulsync, dash, zephyr, arx, verve, flux, xent
 *   size: 'sidebar' | 'header' | 'dashboard' | 'mobile'
 */

// `preserveCase: true` opts the label out of the shared textTransform:
// 'uppercase' styling so the rendered text matches the literal casing in
// `before` / `after`. Used for brand names where a specific letter must
// stay lowercase, like xeNT.
const APP_CONFIG = {
  uncode:   { logo: '/logo-uncode.png',   before: '',    after: 'NCODE',      label: 'Uncode' },
  giraffe:  { logo: '/logo-giraffe.png',  before: '',    after: 'IRAFFE',     label: 'Giraffe' },
  framed:   { logo: '/logo-framed.png',   before: '',    after: 'RAMED',      label: 'Framed' },
  cult:     { logo: '/logo-cult.png',     before: '',    after: 'ULT OF ONE', label: 'Cult of One' },
  soulsync: { logo: '/logo-soulsync.png', before: '',    after: 'OULSYNC',    label: 'SoulSync' },
  dash:     { logo: '/logo-dash.png',     before: '',    after: 'ASH',        label: 'Dash' },
  zephyr:   { logo: '/logo-zephyr.png',   before: '',    after: 'EPHYR',      label: 'Zephyr' },
  arx:      { logo: '/logo-arx.png',      before: '',    after: 'RX ACTA',    label: 'Arx Acta' },
  verve:    { logo: '/logo-verve.png',    before: '',    after: 'ERVE',       label: 'Verve' },
  flux:     { logo: '/logo-flux.png',     before: '',    after: 'LUX',        label: 'Flux' },
  xent:     { logo: '/logo-xent.png',     before: '',    after: 'eNT',        label: 'xeNT', preserveCase: true },
};

const SIZES = {
  sidebar:   { badge: 26, fontSize: 13, gap: 2 },
  header:    { badge: 36, fontSize: 28, gap: 2 },
  dashboard: { badge: 32, fontSize: 14, gap: 2 },
  mobile:    { badge: 64, fontSize: 24, gap: 4 },
};

/**
 * iOS squircle (n=4 superellipse) as an SVG clipPath.
 * Uses objectBoundingBox so it scales to any element size.
 * Control points at 0.045/0.955 produce the continuous curvature
 * that distinguishes the squircle from a standard border-radius.
 */
const SQUIRCLE_PATH = 'M 1 0.5 C 1 0.955 0.955 1 0.5 1 C 0.045 1 0 0.955 0 0.5 C 0 0.045 0.045 0 0.5 0 C 0.955 0 1 0.045 1 0.5 Z';

/**
 * Renders the hidden SVG defs block once. Uses a global ID so other
 * components can reference `clipPath: 'url(#squircle)'` without
 * embedding their own SVG.
 */
export function SquircleDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
      <defs>
        <clipPath id="squircle" clipPathUnits="objectBoundingBox">
          <path d={SQUIRCLE_PATH} />
        </clipPath>
      </defs>
    </svg>
  );
}

export default function Logotype({ app, size = 'header' }) {
  const config = APP_CONFIG[app];
  const dims = SIZES[size];
  if (!config || !dims) return null;

  const textStyle = {
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: dims.fontSize,
    fontWeight: size === 'sidebar' ? 500 : 300,
    letterSpacing: '0.18em',
    textTransform: config.preserveCase ? 'none' : 'uppercase',
    color: '#f0f0f5',
    lineHeight: 1,
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: dims.gap }}>
      {config.before && <span style={textStyle}>{config.before}</span>}
      <img
        src={config.logo}
        alt={config.label}
        style={{
          width: dims.badge,
          height: dims.badge,
          clipPath: 'url(#squircle)',
          flexShrink: 0,
        }}
      />
      {config.after && <span style={textStyle}>{config.after}</span>}
    </span>
  );
}
